import React, { useEffect, useRef, useState } from "react";
import { Box, Portal } from "@chakra-ui/react";
import { TourGuideTooltip, TourGuideTooltipProps } from "./TourGuideTooltip";
import { TourStep } from "@root/hooks/useTourGuide";

export interface TourGuideProps {
  isActive: boolean;
  currentStep: TourStep | null;
  currentStepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
  targetRef?: React.RefObject<HTMLElement>;
  targetSelector?: string;
  borderRadius?: string;
}

export const TourGuide: React.FC<TourGuideProps> = ({
  isActive,
  currentStep,
  currentStepIndex,
  totalSteps,
  onNext,
  onBack,
  onClose,
  targetRef,
  targetSelector,
  borderRadius = "8px",
}) => {
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [highlightBox, setHighlightBox] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  // Calculate position for tooltip and highlight box
  useEffect(() => {
    if (!isActive || !currentStep) return;

    const updatePosition = () => {
      let element: HTMLElement | null = null;

      // Try to get element from ref first, then selector
      if (targetRef?.current) {
        element = targetRef.current;
      } else if (targetSelector) {
        element = document.querySelector(targetSelector) as HTMLElement;
      } else if (currentStep.targetSelector) {
        element = document.querySelector(
          currentStep.targetSelector
        ) as HTMLElement;
      } else if (currentStep.targetRef?.current) {
        element = currentStep.targetRef.current;
      }

      if (!element) return;

      const rect = element.getBoundingClientRect();
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      // Set highlight box position
      setHighlightBox({
        top: rect.top + scrollY,
        left: rect.left + scrollX,
        width: rect.width,
        height: rect.height,
      });

      // Calculate tooltip position (to the right of the element)
      const position = currentStep.position || "right";
      const offset = currentStep.offset || { x: 20, y: 0 };

      let tooltipTop = rect.top + scrollY;
      let tooltipLeft = rect.left + scrollX;

      switch (position) {
        case "right":
          tooltipLeft = rect.right + scrollX + offset.x;
          tooltipTop = rect.top + scrollY + offset.y;
          break;
        case "left":
          tooltipLeft = rect.left + scrollX - offset.x - 500; // Approximate tooltip width
          tooltipTop = rect.top + scrollY + offset.y;
          break;
        case "top":
          tooltipLeft = rect.left + scrollX + offset.x;
          tooltipTop = rect.top + scrollY - offset.y - 200; // Approximate tooltip height
          break;
        case "bottom":
          tooltipLeft = rect.left + scrollX + offset.x;
          tooltipTop = rect.bottom + scrollY + offset.y;
          break;
        case "center":
          tooltipLeft = window.innerWidth / 2 - 250; // Center tooltip
          tooltipTop = window.innerHeight / 2 - 150;
          break;
      }

      // Ensure tooltip stays within viewport
      const tooltipWidth = 500;
      const tooltipHeight = 200;
      if (tooltipLeft + tooltipWidth > window.innerWidth) {
        tooltipLeft = window.innerWidth - tooltipWidth - 20;
      }
      if (tooltipLeft < 0) {
        tooltipLeft = 20;
      }
      if (tooltipTop + tooltipHeight > window.innerHeight + scrollY) {
        tooltipTop = window.innerHeight + scrollY - tooltipHeight - 20;
      }
      if (tooltipTop < scrollY) {
        tooltipTop = scrollY + 20;
      }

      setTooltipPosition({ top: tooltipTop, left: tooltipLeft });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isActive, currentStep, targetRef, targetSelector]);

  if (!isActive || !currentStep || !tooltipPosition) {
    return null;
  }

  const showBack = currentStepIndex > 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const showNext = true; // Always show next/finish button

  return (
    <Portal>
      {/* Blur overlay - only shown when no highlight box */}
      {!highlightBox && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.5)"
          backdropFilter="blur(1px)"
          zIndex={9998}
          onClick={onClose}
        />
      )}

      {/* Highlight box with border */}
      {highlightBox && (
        <>
          {/* Top overlay */}
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            height={`${highlightBox.top}px`}
            bg="rgba(0, 0, 0, 0.5)"
            backdropFilter="blur(1px)"
            zIndex={9999}
            onClick={onClose}
            cursor="pointer"
          />
          {/* Bottom overlay */}
          <Box
            position="fixed"
            top={`${highlightBox.top + highlightBox.height}px`}
            left="0"
            right="0"
            bottom="0"
            bg="rgba(0, 0, 0, 0.5)"
            backdropFilter="blur(1px)"
            zIndex={9999}
            onClick={onClose}
            cursor="pointer"
          />
          {/* Left overlay */}
          <Box
            position="fixed"
            top={`${highlightBox.top}px`}
            left="0"
            width={`${highlightBox.left}px`}
            height={`${highlightBox.height}px`}
            bg="rgba(0, 0, 0, 0.5)"
            backdropFilter="blur(1px)"
            zIndex={9999}
            onClick={onClose}
            cursor="pointer"
          />
          {/* Right overlay */}
          <Box
            position="fixed"
            top={`${highlightBox.top}px`}
            left={`${highlightBox.left + highlightBox.width}px`}
            right="0"
            height={`${highlightBox.height}px`}
            bg="rgba(0, 0, 0, 0.5)"
            backdropFilter="blur(1px)"
            zIndex={9999}
            onClick={onClose}
            cursor="pointer"
          />
          {/* Highlight border */}
          <Box
            position="fixed"
            top={`${highlightBox.top}px`}
            left={`${highlightBox.left}px`}
            width={`${highlightBox.width}px`}
            height={`${highlightBox.height}px`}
            border="3px solid white"
            borderRadius={borderRadius}
            zIndex={10000}
            pointerEvents="none"
            boxShadow="0 0 0 2px rgba(255, 255, 255, 0.3)"
          />
        </>
      )}

      {/* Tooltip */}
      <Box
        ref={tooltipRef}
        position="absolute"
        top={`${tooltipPosition.top}px`}
        left={`${tooltipPosition.left}px`}
        zIndex={10000}
      >
        <TourGuideTooltip
          title={currentStep.title}
          description={currentStep.description}
          currentStep={currentStepIndex + 1}
          totalSteps={totalSteps}
          onNext={isLastStep ? onClose : onNext}
          onBack={onBack}
          onClose={onClose}
          showBack={showBack}
          showNext={showNext}
        />
      </Box>
    </Portal>
  );
};
